import React from 'react';

export interface DifficultyGaugeProps {
  teamAvg: number | null;
  opponentAvg: number | null;
  className?: string;
  /** 'md' (default) matches the original 48x27 gauge. 'sm' is a compact variant
   *  for dense per-line contexts. */
  size?: 'sm' | 'md';
  /** Whether to render the textual category label beneath the gauge. */
  showLabel?: boolean;
  /** Tailwind text-color class for the needle/ticks (uses currentColor). Override
   *  for dark backgrounds (e.g. tooltips). Defaults to the neutral gray pair. */
  svgClassName?: string;
}

const SIZES: Record<'sm' | 'md', { width: number; height: number; labelClass: string }> = {
  md: { width: 48, height: 27, labelClass: 'text-[10px]' },
  sm: { width: 28, height: 16, labelClass: 'text-[8px]' },
};

const MAX_DIFF = 0.5;

const computeAngleDeg = (diff: number): number => {
  const clamped = Math.max(-MAX_DIFF, Math.min(MAX_DIFF, diff));
  return 90 - (clamped / MAX_DIFF) * 90;
};

const describeDifficulty = (diff: number): string => {
  if (Math.abs(diff) < 0.01) return 'evenly matched';
  const magnitude = Math.abs(diff).toFixed(2);
  return diff > 0 ? `${magnitude} harder than your team average` : `${magnitude} easier than your team average`;
};

export type DifficultyCategory =
  | 'much-easier'
  | 'easier'
  | 'even'
  | 'harder'
  | 'much-harder';

const categorize = (diff: number): DifficultyCategory => {
  const rounded = Math.round(diff * 100) / 100;
  const abs = Math.abs(rounded);
  if (abs < 0.1) return 'even';
  if (rounded < 0) return abs >= 0.3 ? 'much-easier' : 'easier';
  return abs >= 0.3 ? 'much-harder' : 'harder';
};

const CATEGORY_LABELS: Record<DifficultyCategory, string> = {
  'much-easier': 'Much Easier',
  easier: 'Easier',
  even: 'Even',
  harder: 'Harder',
  'much-harder': 'Much Harder',
};

const CATEGORY_TEXT_CLASSES: Record<DifficultyCategory, string> = {
  'much-easier': 'text-green-700 dark:text-green-400',
  easier: 'text-green-600 dark:text-green-400',
  even: 'text-gray-500 dark:text-gray-400',
  harder: 'text-orange-600 dark:text-orange-400',
  'much-harder': 'text-orange-700 dark:text-orange-500',
};

const DifficultyGauge: React.FC<DifficultyGaugeProps> = ({
  teamAvg,
  opponentAvg,
  className,
  size = 'md',
  showLabel = true,
  svgClassName = 'text-gray-700 dark:text-gray-200',
}) => {
  if (teamAvg === null || opponentAvg === null || Number.isNaN(teamAvg) || Number.isNaN(opponentAvg)) {
    return null;
  }

  const { width: svgWidth, height: svgHeight, labelClass } = SIZES[size];

  const diff = opponentAvg - teamAvg;
  const angleDeg = computeAngleDeg(diff);
  const angleRad = (angleDeg * Math.PI) / 180;

  const cx = 32;
  const cy = 32;
  const arcRadius = 26;
  const needleRadius = 22;

  const tipX = cx + needleRadius * Math.cos(angleRad);
  const tipY = cy - needleRadius * Math.sin(angleRad);

  const arcLeftX = cx - arcRadius;
  const arcRightX = cx + arcRadius;
  const arcTopX = cx;
  const arcTopY = cy - arcRadius;
  const trackPath = `M ${arcLeftX} ${cy} A ${arcRadius} ${arcRadius} 0 0 1 ${arcRightX} ${cy}`;

  const category = categorize(diff);

  const arcEndX = cx + arcRadius * Math.cos(angleRad);
  const arcEndY = cy - arcRadius * Math.sin(angleRad);

  let activePath: string | null = null;
  let activeColor = '';
  if (category === 'easier' || category === 'much-easier') {
    activePath = `M ${arcTopX} ${arcTopY} A ${arcRadius} ${arcRadius} 0 0 0 ${arcEndX} ${arcEndY}`;
    activeColor = '#22c55e';
  } else if (category === 'harder' || category === 'much-harder') {
    activePath = `M ${arcTopX} ${arcTopY} A ${arcRadius} ${arcRadius} 0 0 1 ${arcEndX} ${arcEndY}`;
    activeColor = '#f97316';
  }

  const categoryLabel = CATEGORY_LABELS[category];
  const ariaLabel =
    category === 'even'
      ? 'Opponent difficulty: Even'
      : `Opponent difficulty: ${categoryLabel}, ${describeDifficulty(diff)}`;

  return (
    <div
      className={`flex flex-col items-center gap-0.5 ${className ?? ''}`}
      role="img"
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      <svg
        viewBox="0 0 64 36"
        width={svgWidth}
        height={svgHeight}
        className={svgClassName}
        data-testid="difficulty-gauge"
        data-angle={angleDeg.toFixed(2)}
      >
        <path
          d={trackPath}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={5}
          strokeLinecap="round"
        />

        {activePath && (
          <path
            d={activePath}
            fill="none"
            stroke={activeColor}
            strokeWidth={5}
            strokeLinecap="round"
            data-testid="difficulty-gauge-active-arc"
          />
        )}

        <line x1={arcLeftX} y1={cy} x2={arcLeftX} y2={cy + 3} stroke="#9ca3af" strokeWidth={1} />
        <line x1={cx} y1={cy - arcRadius - 3} x2={cx} y2={cy - arcRadius} stroke="#9ca3af" strokeWidth={1} />
        <line x1={arcRightX} y1={cy} x2={arcRightX} y2={cy + 3} stroke="#9ca3af" strokeWidth={1} />

        <line
          x1={cx}
          y1={cy}
          x2={tipX}
          y2={tipY}
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          data-testid="difficulty-gauge-needle"
        />
        <circle cx={cx} cy={cy} r={2.5} fill="currentColor" />
      </svg>
      {showLabel && (
        <span
          className={`${labelClass} font-medium leading-none ${CATEGORY_TEXT_CLASSES[category]}`}
          data-testid="difficulty-gauge-category"
        >
          {categoryLabel}
        </span>
      )}
    </div>
  );
};

export default DifficultyGauge;
